// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// Patent App No. ZA 2026/04523 · Priority Date: 08 October 2025
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import axios from "axios";
import {
  Network,
  Play,
  RefreshCw,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Copy,
  Zap,
  Lock,
  FileJson,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (a) =>
  new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
  }).format(a || 0);

const STATUS_COLORS = {
  unconfigured: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  pending_approval: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  test: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  staging: "bg-purple-500/20 text-purple-300 border-purple-500/30",
  active: "bg-green-500/20 text-green-300 border-green-500/30",
  suspended: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  failed: "bg-red-500/20 text-red-300 border-red-500/30",
};

const OUTCOME_COLORS = {
  accepted: "text-green-400",
  rejected: "text-red-400",
  pending: "text-yellow-400",
  timeout: "text-orange-400",
  duplicate: "text-cyan-400",
  schema_invalid: "text-red-500",
  circuit_open: "text-red-500",
};

const RailAdapters = () => {
  const [rails, setRails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dispatching, setDispatching] = useState(false);
  const [selectedRail, setSelectedRail] = useState("");
  const [amount, setAmount] = useState("1000");
  const [currency, setCurrency] = useState("ZAR");
  const [dispatchResp, setDispatchResp] = useState(null);
  const [recon, setRecon] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("360wallet_token");
      const [railsRes, reconRes] = await Promise.all([
        axios.get(`${API_URL}/api/rails`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/rails/reconciliation/summary`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setRails(railsRes.data.rails || []);
      setRecon(reconRes.data.reconciliation || {});
    } catch (e) {
      toast.error("Failed to load rails");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dispatch = async () => {
    if (!selectedRail) {
      toast.error("Select a rail first");
      return;
    }
    setDispatching(true);
    setDispatchResp(null);
    try {
      const token = localStorage.getItem("360wallet_token");
      const res = await axios.post(
        `${API_URL}/api/simulator/rails/dispatch`,
        {
          rail_id: selectedRail,
          amount: parseFloat(amount),
          currency,
          debtor_name: "360Wallet Sender",
          creditor_name: "360Wallet Receiver",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDispatchResp(res.data);
      if (res.data.outcome === "accepted") {
        toast.success(`${res.data.rail_id}: ACCEPTED`);
      } else if (res.data.outcome === "duplicate") {
        toast.info("Duplicate — idempotency prevented reprocessing");
      } else if (res.data.outcome === "circuit_open") {
        toast.warning("Rail is fail-closed — pending approval");
      } else {
        toast.warning(`Outcome: ${res.data.outcome}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setDispatchResp({ error: e.response?.data?.detail || e.message });
    } finally {
      setDispatching(false);
    }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  if (loading && rails.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rail-adapters-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
            <Network className="h-6 w-6 text-primary" />
            National Utility & Regional Clearing Rails
          </h1>
          <p className="text-muted-foreground">
            ISO 20022 orchestration to PayInc, SAMOS, SIRESS, TCIB · all adapters fail-closed pending regulatory approval
          </p>
        </div>
        <Button variant="outline" onClick={load}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Fail-closed banner */}
      <Card className="bg-yellow-500/10 border-yellow-500/30">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium mb-1 text-yellow-300">
              All Rails Fail-Closed by Design
            </div>
            <div className="text-muted-foreground">
              No adapter can transmit to a production clearing system until the listed external dependencies
              (PayInc onboarding, SARB NPSD approval, SADC-RTGS sponsorship, TCIB scheme sign-off) are
              supplied and the rail is explicitly activated from this dashboard. This is required by the
              development directive — no simulated connectivity is represented as live.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rails Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {rails.map((r) => (
          <Card key={r.rail_id} className="bg-card border-border">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Network className="h-5 w-5 text-primary" />
                </div>
                <Badge className={`border ${STATUS_COLORS[r.status] || ""}`}>
                  {r.status.replace(/_/g, " ")}
                </Badge>
              </div>
              <CardTitle className="font-heading text-lg mt-3">
                {r.rail_id}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">{r.display_name}</p>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Environment</span>
                  <span className="font-mono">{r.environment}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Messages</span>
                  <span className="font-mono">{r.supported_messages.length} types</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pending Deps</span>
                  <span className={r.external_deps_pending.length > 0 ? "text-yellow-400 font-bold" : "text-green-400"}>
                    {r.external_deps_pending.length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">mTLS Bound</span>
                  {r.mtls_bound ? (
                    <ShieldCheck className="h-3.5 w-3.5 text-green-400" />
                  ) : (
                    <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </div>
              </div>
              {r.external_deps_pending.length > 0 && (
                <div className="p-2 rounded bg-secondary/50 border border-border">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Pending Confirmations
                  </div>
                  <ul className="text-[11px] space-y-0.5 text-muted-foreground">
                    {r.external_deps_pending.map((d) => (
                      <li key={d}>· {d}</li>
                    ))}
                  </ul>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setSelectedRail(r.rail_id)}
                data-testid={`select-rail-${r.rail_id.toLowerCase()}`}
              >
                Select Rail
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dispatch + Reconciliation Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dispatch Form */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Play className="h-5 w-5 text-primary" />
              Dispatch ISO 20022 Message (Sandbox)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Target Rail</Label>
              <Select value={selectedRail} onValueChange={setSelectedRail}>
                <SelectTrigger data-testid="rail-select">
                  <SelectValue placeholder="Select rail" />
                </SelectTrigger>
                <SelectContent>
                  {rails.map((r) => (
                    <SelectItem key={r.rail_id} value={r.rail_id}>
                      {r.rail_id} — {r.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Amount (ZAR)</Label>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="dispatch-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Input value={currency} disabled />
              </div>
            </div>
            <Button
              className="w-full"
              onClick={dispatch}
              disabled={dispatching || !selectedRail}
              data-testid="dispatch-btn"
            >
              {dispatching ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Dispatching…
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" /> Dispatch via {selectedRail || "…"}
                </>
              )}
            </Button>

            {dispatchResp && (
              <div className="mt-4 p-4 rounded-md bg-secondary/30 border border-border space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Outcome</span>
                  <span className={`font-mono font-bold text-sm ${OUTCOME_COLORS[dispatchResp.outcome] || ""}`}>
                    {dispatchResp.outcome?.toUpperCase() || "ERROR"}
                  </span>
                </div>
                <div className="text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rail</span>
                    <span className="font-mono">{dispatchResp.rail_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Correlation</span>
                    <span className="font-mono flex items-center gap-1">
                      {dispatchResp.correlation_id}
                      <Button variant="ghost" size="icon" className="h-4 w-4" onClick={() => copy(dispatchResp.correlation_id)}>
                        <Copy className="h-2.5 w-2.5" />
                      </Button>
                    </span>
                  </div>
                  {dispatchResp.reason_code && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Code</span>
                      <span className="font-mono">{dispatchResp.reason_code}</span>
                    </div>
                  )}
                  {dispatchResp.reason_narrative && (
                    <div className="pt-2 text-muted-foreground border-t border-border/50">
                      {dispatchResp.reason_narrative}
                    </div>
                  )}
                </div>
                <Separator />
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground flex items-center gap-1">
                    <FileJson className="h-3.5 w-3.5" /> View ISO 20022 pacs.008 payload
                  </summary>
                  <pre className="mt-2 p-2 rounded bg-slate-950 text-[10px] overflow-x-auto font-mono text-gray-300">
                    {JSON.stringify(dispatchResp.message_payload, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reconciliation */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Reconciliation Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(recon || {}).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                No rail transactions recorded yet — dispatch a sandbox message above.
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(recon).map(([rail, outcomes]) => (
                  <div key={rail} className="p-3 rounded-md bg-secondary/30 border border-border">
                    <div className="font-mono font-bold text-sm mb-2">{rail}</div>
                    <div className="flex gap-3 flex-wrap text-xs">
                      {Object.entries(outcomes).map(([o, count]) => (
                        <span key={o} className={`flex items-center gap-1 ${OUTCOME_COLORS[o] || ""}`}>
                          {o === "accepted" && <CheckCircle2 className="h-3 w-3" />}
                          {(o === "rejected" || o === "circuit_open") && <XCircle className="h-3 w-3" />}
                          {o === "pending" && <Clock className="h-3 w-3" />}
                          {o === "duplicate" && <Zap className="h-3 w-3" />}
                          {o}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RailAdapters;
